import { CheckCircle2, MessageSquare, Percent, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
	useExportDirecionadaAnswers,
	useFreeTextMetrics,
} from '@/api/queries/kpi-metrics-queries'
import { type ExportFormat, ExportMenu } from '@/components/commons/export-menu'
import { NativeSelect } from '@/components/ui/native-select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store'
import type { KpiFreeTextTableParams, KpiMetricsParams } from '@/types'
import { downloadBlob } from '@/utils/download-blob'
import { formatDateToDisplay } from '@/utils/formatDate'
import { BarListRow } from './shared/bar-list-row'
import { KpiMetricsDashboardLoading } from './shared/kpi-metrics-dashboard-loading'
import { KpiStatCard } from './shared/kpi-stat-card'

const EXPORT_ROLES = ['ADMIN', 'SSMA_MANAGER']

const firstId = (csv?: string) => (csv && !csv.includes(',') ? csv : undefined)

const formatPct = (value: number) => `${value.toFixed(1).replace('.0', '')}%`

export function FreeTextDashboard({ params }: { params?: KpiMetricsParams }) {
	const { t } = useTranslation()
	const role = useAuthStore((s) => s.user?.role)
	const [quizId, setQuizId] = useState('')
	const [status, setStatus] = useState<
		KpiFreeTextTableParams['personStatus'] | ''
	>('')
	const [jobRole, setJobRole] = useState('')
	const [branchId, setBranchId] = useState('')
	const { data, isLoading } = useFreeTextMetrics({
		...params,
		...(quizId ? { quizId } : {}),
		...(status ? { personStatus: status } : {}),
		...(jobRole ? { personJobRole: jobRole } : {}),
		...(branchId ? { personBranchId: branchId } : {}),
	})
	const exportMutation = useExportDirecionadaAnswers()

	if (isLoading || !data) return <KpiMetricsDashboardLoading />

	const canExport = role ? EXPORT_ROLES.includes(role) : false

	const handleExport = (format: ExportFormat) => {
		if (format === 'pdf') return
		exportMutation.mutate(
			{
				format,
				kind: 'FREE_TEXT',
				...(quizId ? { quizId } : {}),
				branchId: firstId(params?.branchIds),
				crId: firstId(params?.crIds),
				startDate: params?.rangeStart,
				endDate: params?.rangeEnd,
			},
			{
				onSuccess: (blob) => downloadBlob(blob, `respostas-livres.${format}`),
				onError: () => toast.error(t('common.exportError')),
			},
		)
	}

	const people = data.people
	const maxQuiz = Math.max(1, ...data.byQuiz.map((q) => q.submissions))

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<div className="w-full sm:w-72">
					<label
						htmlFor="free-text-quiz"
						className="mb-1 block text-xs font-medium text-kaefer-dark-grey"
					>
						{t('kpiMetrics.freeText.quiz')}
					</label>
					<NativeSelect
						id="free-text-quiz"
						value={quizId}
						onChange={(e) => setQuizId(e.target.value)}
					>
						<option value="">{t('kpiMetrics.freeText.allQuizzes')}</option>
						{data.quizzes.map((quiz) => (
							<option key={quiz.id} value={quiz.id}>
								{quiz.title}
							</option>
						))}
					</NativeSelect>
				</div>
				{canExport && (
					<ExportMenu
						formats={['xlsx', 'csv']}
						onExport={handleExport}
						isExporting={exportMutation.isPending}
					/>
				)}
			</div>
			{canExport && (
				<p className="-mt-2 text-xs text-muted-foreground">
					{t('kpiMetrics.freeText.exportHint')}
				</p>
			)}

			{data.quizzes.length === 0 ? (
				<p className="rounded-lg border border-kaefer-light-silver bg-white p-4 text-sm text-muted-foreground">
					{t('kpiMetrics.freeText.noQuizzes')}
				</p>
			) : (
				<>
					<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
						<KpiStatCard
							icon={<Percent className="size-4" />}
							iconClass="bg-kaefer-violet/15 text-kaefer-violet"
							label={t('kpiMetrics.freeText.participation')}
							value={formatPct(data.totals.participationPct)}
						/>
						<KpiStatCard
							icon={<Users className="size-4" />}
							iconClass="bg-kaefer-dark-grey/10 text-kaefer-dark-grey"
							label={t('kpiMetrics.freeText.peopleInScope')}
							value={String(data.totals.peopleInScope)}
						/>
						<KpiStatCard
							icon={<CheckCircle2 className="size-4" />}
							iconClass="bg-kaefer-forest-green/15 text-kaefer-forest-green"
							label={t('kpiMetrics.freeText.respondents')}
							value={String(data.totals.respondents)}
						/>
						<KpiStatCard
							icon={<MessageSquare className="size-4" />}
							iconClass="bg-kaefer-sapphire-blue/15 text-kaefer-sapphire-blue"
							label={t('kpiMetrics.freeText.submissions')}
							value={String(data.totals.submissions)}
						/>
					</div>

					<div className="grid gap-4 lg:grid-cols-3">
						<BarCard
							title={t('kpiMetrics.freeText.byQuiz')}
							empty={t('kpiMetrics.freeText.empty')}
						>
							{data.byQuiz.map((q) => (
								<BarListRow
									key={q.quizId}
									label={q.quizTitle}
									displayValue={`${q.submissions}`}
									widthPct={(q.submissions / maxQuiz) * 100}
									colorClass="bg-kaefer-violet"
								/>
							))}
						</BarCard>
						<BarCard
							title={t('kpiMetrics.freeText.byBranch')}
							empty={t('kpiMetrics.freeText.empty')}
						>
							{data.byBranch.map((b) => (
								<BarListRow
									key={b.branchId}
									label={b.branchName}
									displayValue={`${formatPct(b.participationPct)} (${b.respondents}/${b.people})`}
									widthPct={b.participationPct}
									colorClass="bg-kaefer-forest-green"
									onClick={() => setBranchId(b.branchId)}
								/>
							))}
						</BarCard>
						<BarCard
							title={t('kpiMetrics.freeText.byJobRole')}
							empty={t('kpiMetrics.freeText.empty')}
						>
							{data.byJobRole.map((j) => (
								<BarListRow
									key={j.jobRole}
									label={j.jobRole}
									displayValue={`${formatPct(j.participationPct)} (${j.respondents}/${j.people})`}
									widthPct={j.participationPct}
									colorClass="bg-kaefer-sapphire-blue"
									onClick={() => setJobRole(j.jobRole)}
								/>
							))}
						</BarCard>
					</div>

					<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
						<div className="mb-3 flex flex-col gap-3">
							<span className="text-sm font-semibold text-kaefer-dark-grey">
								{t('kpiMetrics.freeText.people')}
							</span>
							<div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
								<SearchableSelect
									options={data.jobRoles.map((role) => ({
										value: role,
										label: role,
									}))}
									value={jobRole}
									onValueChange={setJobRole}
									placeholder={t('kpiMetrics.freeText.allRoles')}
									searchPlaceholder={t('kpiMetrics.freeText.searchRole')}
									emptyMessage={t('kpiMetrics.freeText.empty')}
								/>
								<SearchableSelect
									options={data.branches.map((branch) => ({
										value: branch.id,
										label: branch.name,
									}))}
									value={branchId}
									onValueChange={setBranchId}
									placeholder={t('kpiMetrics.freeText.allBranches')}
									searchPlaceholder={t('kpiMetrics.freeText.searchBranch')}
									emptyMessage={t('kpiMetrics.freeText.empty')}
								/>
								<NativeSelect
									aria-label={t('kpiMetrics.freeText.status')}
									value={status}
									onChange={(e) =>
										setStatus(
											e.target.value as
												| KpiFreeTextTableParams['personStatus']
												| '',
										)
									}
								>
									<option value="">{t('kpiMetrics.freeText.all')}</option>
									<option value="responded">
										{t('kpiMetrics.freeText.done')}
									</option>
									<option value="notResponded">
										{t('kpiMetrics.freeText.notDone')}
									</option>
								</NativeSelect>
							</div>
						</div>
						<p className="mb-2 text-xs text-muted-foreground">
							{t('kpiMetrics.freeText.summary', {
								responded: data.peopleSummary.responded,
								total: data.peopleSummary.total,
							})}
						</p>
						<div className="max-h-96 overflow-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t('kpiMetrics.freeText.name')}</TableHead>
										<TableHead>{t('kpiMetrics.freeText.badge')}</TableHead>
										<TableHead>{t('kpiMetrics.freeText.jobRole')}</TableHead>
										<TableHead>{t('kpiMetrics.freeText.branch')}</TableHead>
										<TableHead>{t('kpiMetrics.freeText.cr')}</TableHead>
										<TableHead className="text-right">
											{t('kpiMetrics.freeText.submissionsShort')}
										</TableHead>
										<TableHead>
											{t('kpiMetrics.freeText.lastSubmission')}
										</TableHead>
										<TableHead>{t('kpiMetrics.freeText.status')}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{people.map((p) => (
										<TableRow key={p.personId}>
											<TableCell className="font-medium">{p.name}</TableCell>
											<TableCell>{p.badge}</TableCell>
											<TableCell>{p.jobRole ?? '-'}</TableCell>
											<TableCell>{p.branchName}</TableCell>
											<TableCell>{p.crName}</TableCell>
											<TableCell className="text-right tabular-nums">
												{p.submissions}
											</TableCell>
											<TableCell>
												{p.lastSubmissionAt
													? formatDateToDisplay(p.lastSubmissionAt)
													: '-'}
											</TableCell>
											<TableCell>
												<span
													className={cn(
														'rounded-full px-2 py-0.5 text-xs font-semibold',
														p.done
															? 'bg-kaefer-forest-green/15 text-kaefer-forest-green'
															: 'bg-kaefer-medium-grey/20 text-kaefer-dark-grey',
													)}
												>
													{p.done
														? t('kpiMetrics.freeText.done')
														: t('kpiMetrics.freeText.notDone')}
												</span>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
						{data.peopleTruncated && (
							<p className="mt-2 text-xs text-muted-foreground">
								{t('kpiMetrics.freeText.truncated')}
							</p>
						)}
					</div>
				</>
			)}
		</div>
	)
}

function BarCard({
	title,
	empty,
	children,
}: {
	title: string
	empty: string
	children: React.ReactNode[]
}) {
	return (
		<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
			<span className="text-sm font-semibold text-kaefer-dark-grey">
				{title}
			</span>
			<div className="mt-2 flex max-h-72 flex-col gap-1.5 overflow-y-auto pr-1">
				{children.length ? (
					children
				) : (
					<p className="text-xs text-muted-foreground">{empty}</p>
				)}
			</div>
		</div>
	)
}
